import { useEffect, useMemo, useState } from "react";
import { CalendarPlus, CalendarClock, Save, Trash2, Plus, X, Info } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Orientation Dates panel — Seasons tab.
 *
 * Admin can ADD / EDIT / DELETE orientation dates for the active season.
 * Each row has:
 *   • Date
 *   • Audience radio (All / First-Year / Group / Non-Group / Specific divisions / None)
 *   • Division multi-picker (only when audience = "divisions")
 *   • Late After time (optional — blank = no Late marking)
 *
 * Data is scoped to the active season — when leadership flips to the next
 * season, this panel auto-loads only that season's orientation set.
 */

const AUDIENCE_NOTES = {
  all: "Every staff member on the roster is expected.",
  first_year: "Staff with 0–1 years at camp are expected.",
  group: "Skippers, Mariners, and Navigators only.",
  non_group: "Everyone EXCEPT Skippers / Mariners / Navigators.",
  divisions: "Pick the specific divisions that must attend.",
  none: "No auto-Absent. Sign-ins are recorded; everyone else stays \"—\" unless you mark them Absent manually.",
};

export default function OrientationDatesPanel() {
  const [seasonId, setSeasonId] = useState(null);
  const [items, setItems] = useState(null);          // [{date, audience, divisions[], late_threshold_local}]
  const [audienceOptions, setAudienceOptions] = useState([]);
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pendingAddDate, setPendingAddDate] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/admin/orientation-dates");
      setSeasonId(r.data.season_id || null);
      setItems(r.data.items || []);
      setAudienceOptions(r.data.audience_options || []);
      setDivisionOptions(r.data.division_options || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load orientation dates");
    }
  };
  useEffect(() => { load(); }, []);

  const updateRow = (date, patch) => {
    setItems((prev) => prev.map((it) => it.date === date ? { ...it, ...patch } : it));
  };

  const setAudience = (date, audience) => {
    updateRow(date, {
      audience,
      // Clear divisions when leaving the "divisions" preset, so we don't
      // accidentally save stale slugs.
      divisions: audience === "divisions" ? (items.find((i) => i.date === date)?.divisions || []) : [],
    });
  };

  const toggleDivision = (date, slug) => {
    const row = items.find((i) => i.date === date);
    const current = new Set(row.divisions || []);
    if (current.has(slug)) current.delete(slug); else current.add(slug);
    updateRow(date, { divisions: Array.from(current) });
  };

  const addNewRow = () => {
    const d = pendingAddDate.trim();
    if (!d) { toast.error("Pick a date first"); return; }
    if (items.some((it) => it.date === d)) {
      toast.error("That date is already in the list");
      return;
    }
    setItems((prev) => [
      ...prev,
      { date: d, audience: "all", divisions: [], late_threshold_local: null },
    ].sort((a, b) => (a.date || "").localeCompare(b.date || "")));
    setPendingAddDate("");
  };

  const removeRow = (date) => {
    if (!window.confirm(`Remove ${prettyDate(date)} from the orientation list?`)) return;
    setItems((prev) => prev.filter((it) => it.date !== date));
  };

  const save = async () => {
    if (!items) return;
    // Pre-validate: divisions audience needs at least one division.
    for (const it of items) {
      if (it.audience === "divisions" && (!it.divisions || it.divisions.length === 0)) {
        toast.error(`${prettyDate(it.date)}: pick at least one division`);
        return;
      }
    }
    setSaving(true);
    try {
      await api.put("/admin/orientation-dates", {
        items: items.map((it) => ({
          date: it.date,
          audience: it.audience,
          divisions: it.audience === "divisions" ? (it.divisions || []) : [],
          late_threshold_local: it.late_threshold_local || null,
        })),
      });
      toast.success("Orientation dates saved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const sortedItems = useMemo(
    () => (items || []).slice().sort((a, b) => (a.date || "").localeCompare(b.date || "")),
    [items],
  );

  if (!items) return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6">
      <p className="text-sm text-slate-500">Loading…</p>
    </section>
  );

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 lg:col-span-2" data-testid="orientation-dates-panel">
      <div className="flex items-start gap-2 mb-4">
        <CalendarClock className="w-5 h-5 text-blue-600 mt-0.5" />
        <div className="flex-1">
          <h2 className="font-display text-lg font-bold">Orientation Dates</h2>
          <p className="text-sm text-slate-500 mt-1">
            Pre-camp dates when staff are expected to sign in. Pick who&apos;s expected and the optional Late After time for each date.
            {seasonId && <span className="ml-1 text-slate-400">Season: <code className="font-mono">{seasonId}</code></span>}
          </p>
        </div>
      </div>

      {/* Add-a-date row */}
      <div className="flex flex-wrap items-end gap-2 mb-4 p-3 rounded-xl border border-dashed border-blue-300 bg-blue-50/40">
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold mb-1">Add Orientation Date</label>
          <input
            type="date"
            value={pendingAddDate}
            onChange={(e) => setPendingAddDate(e.target.value)}
            className="px-3 py-2 rounded-lg border border-slate-200 text-sm"
            data-testid="orientation-add-date-input"
          />
        </div>
        <button
          onClick={addNewRow}
          className="btn-touch inline-flex items-center gap-2 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2"
          data-testid="orientation-add-date-btn"
        >
          <Plus className="w-4 h-4" /> Add Date
        </button>
        <div className="text-xs text-slate-500 ml-auto self-center">
          Adding a date here drops it into the list below — set the audience and Late time, then click <strong>Save</strong>.
        </div>
      </div>

      {/* Rows */}
      <div className="space-y-3">
        {sortedItems.map((it) => (
          <div key={it.date} className="rounded-xl border border-slate-200 bg-slate-50/40 p-4" data-testid={`orientation-row-${it.date}`}>
            <div className="flex flex-wrap items-start gap-4">
              {/* Date + delete */}
              <div className="min-w-[160px]">
                <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold mb-1">Date</div>
                <div className="font-bold text-slate-900">{prettyDate(it.date)}</div>
                <div className="text-xs text-slate-400 font-mono">{it.date}</div>
              </div>

              {/* Audience radios */}
              <div className="flex-1 min-w-[280px]">
                <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold mb-1.5">Who&apos;s Expected</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {audienceOptions.map((opt) => (
                    <label
                      key={opt.value}
                      className={`flex items-start gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${
                        it.audience === opt.value
                          ? "border-blue-500 bg-blue-50 text-slate-900"
                          : "border-slate-200 bg-white hover:bg-slate-50 text-slate-700"
                      }`}
                      data-testid={`orientation-aud-${it.date}-${opt.value}`}
                    >
                      <input
                        type="radio"
                        name={`aud-${it.date}`}
                        checked={it.audience === opt.value}
                        onChange={() => setAudience(it.date, opt.value)}
                        className="mt-0.5"
                      />
                      <span className="font-medium">{opt.label}</span>
                    </label>
                  ))}
                </div>
                <div className="mt-2 text-xs text-slate-500 flex items-start gap-1">
                  <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>{AUDIENCE_NOTES[it.audience] || ""}</span>
                </div>

                {/* Division multi-picker — only when audience=divisions */}
                {it.audience === "divisions" && (
                  <div className="mt-3 p-3 rounded-lg border border-blue-200 bg-blue-50/60" data-testid={`orientation-divpicker-${it.date}`}>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-blue-900 font-bold mb-1.5">Divisions</div>
                    <div className="flex flex-wrap gap-1.5">
                      {divisionOptions.map((d) => {
                        const checked = (it.divisions || []).includes(d.slug);
                        return (
                          <button
                            key={d.slug}
                            type="button"
                            onClick={() => toggleDivision(it.date, d.slug)}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                              checked
                                ? "bg-blue-600 border-blue-700 text-white"
                                : "bg-white border-slate-200 text-slate-700 hover:border-blue-300"
                            }`}
                            data-testid={`orientation-div-${it.date}-${d.slug}`}
                          >
                            {d.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Late time + delete */}
              <div className="flex flex-col items-end gap-2 min-w-[160px]">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold mb-1">Late After</div>
                  <div className="flex items-center gap-1">
                    <input
                      type="time"
                      value={it.late_threshold_local || ""}
                      onChange={(e) => updateRow(it.date, { late_threshold_local: e.target.value })}
                      className="px-2 py-1.5 rounded-lg border border-slate-200 tabular-nums text-sm w-[120px]"
                      data-testid={`orientation-time-${it.date}`}
                    />
                    {it.late_threshold_local && (
                      <button
                        onClick={() => updateRow(it.date, { late_threshold_local: "" })}
                        className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                        title="Clear — no Late marking on this day"
                        data-testid={`orientation-clear-time-${it.date}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">Blank = no Late marking</div>
                </div>
                <button
                  onClick={() => removeRow(it.date)}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-red-700 hover:bg-red-50 font-semibold"
                  data-testid={`orientation-delete-${it.date}`}
                >
                  <Trash2 className="w-3.5 h-3.5" /> Remove date
                </button>
              </div>
            </div>
          </div>
        ))}
        {sortedItems.length === 0 && (
          <div className="rounded-xl bg-white border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
            No orientation dates configured for this season. Add one above.
          </div>
        )}
      </div>

      <button
        onClick={save}
        disabled={saving}
        className="mt-5 btn-touch inline-flex items-center gap-2 px-5 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-2.5"
        data-testid="orientation-dates-save"
      >
        <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Orientation Dates"}
      </button>
    </section>
  );
}

function prettyDate(iso) {
  try {
    const dt = new Date(iso + "T12:00:00");
    return dt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
}
